import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import debounce from 'lodash.debounce';
import AuthContext from '../../../context/AuthContext';
import socket from '../../../socket';
import { getCalendarEvents } from '../../../services/CalendarServices';
import { addDays } from '../../../utils/dateUtils';

/** How long a fetched window counts as current before a focus or revisit refetches it. */
export const FRESH_MS = 60_000;
const CACHE_LIMIT = 6;
/** Coalesce a burst of change broadcasts into one refetch... */
const CHANGE_DEBOUNCE_MS = 500;
/** ...and spread a classroom's worth of clients out so they don't all refetch at once. */
const CHANGE_JITTER_MS = 2000;

const EMPTY = Object.freeze({ classrooms: [], events: [], truncated: false });

/**
 * Recently fetched windows, kept across mounts so coming back to the calendar (say, Back
 * from an assignment) renders instantly. Keyed by user so switching accounts never shows
 * someone else's calendar.
 */
const cache = new Map();

const cacheKey = (userId, range) => `${userId}|${range.start}|${range.end}`;

const remember = (key, data) => {
    cache.delete(key);
    cache.set(key, { data, fetchedAt: Date.now() });
    while (cache.size > CACHE_LIMIT) cache.delete(cache.keys().next().value);
};

const forgetUser = (userId) => {
    for (const key of [...cache.keys()]) {
        if (key.startsWith(`${userId}|`)) cache.delete(key);
    }
};

/** For tests. */
export const clearCalendarCache = () => cache.clear();

const isCancelled = (err) => err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED';

/**
 * The calendar's data for whatever window is on screen.
 *
 * Call `setRange` with the visible window (FullCalendar's `datesSet`); the hook pads it by a
 * day either side — a due date set in another timezone can sit just past the edge — and
 * fetches it. Cached windows show immediately and are revalidated when stale. Responses
 * that arrive after a newer request started are dropped, so paging quickly through months
 * never flashes an old one.
 *
 * Stays current by refetching when the tab regains focus with stale data, and when the
 * server broadcasts `calendarChanged` to this user.
 */
const useCalendarEvents = () => {
    const { userId } = useContext(AuthContext);
    const [range, setRangeState] = useState(null);
    const [data, setData] = useState(EMPTY);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    // False until the first window arrives, so callers can tell 'nothing yet' from 'empty'.
    const [loaded, setLoaded] = useState(false);

    const rangeRef = useRef(null);
    const requestIdRef = useRef(0);
    const abortRef = useRef(null);

    const setRange = useCallback(({ start, end }) => {
        const next = {
            start: addDays(start, -1).toISOString(),
            end: addDays(end, 1).toISOString(),
        };
        setRangeState((prev) => (prev && prev.start === next.start && prev.end === next.end ? prev : next));
    }, []);

    const load = useCallback(async (target, { force = false } = {}) => {
        if (!target || !userId) return;

        // Whatever happens below, anything still in flight is now out of date.
        abortRef.current?.abort();
        const requestId = ++requestIdRef.current;

        const key = cacheKey(userId, target);
        const cached = cache.get(key);
        if (cached) {
            setData(cached.data);
            setError(null);
            setLoaded(true);
            if (!force && Date.now() - cached.fetchedAt < FRESH_MS) {
                setLoading(false);
                return;
            }
        }

        const controller = new AbortController();
        abortRef.current = controller;
        setLoading(true);

        try {
            const result = await getCalendarEvents(target, { signal: controller.signal });
            remember(key, result);
            if (requestId !== requestIdRef.current) return;
            setData(result);
            setError(null);
            setLoaded(true);
        } catch (err) {
            if (requestId !== requestIdRef.current || isCancelled(err)) return;
            setError(err.response?.data?.error || err.message || 'Failed to load the calendar.');
        } finally {
            if (requestId === requestIdRef.current) setLoading(false);
        }
    }, [userId]);

    useEffect(() => {
        rangeRef.current = range;
        load(range);
    }, [range, load]);

    const refetch = useCallback(() => load(rangeRef.current, { force: true }), [load]);

    // Coming back to the tab: refetch only if what's on screen has gone stale.
    useEffect(() => {
        const handleReturn = () => {
            if (document.visibilityState === 'hidden' || !rangeRef.current || !userId) return;
            const cached = cache.get(cacheKey(userId, rangeRef.current));
            if (!cached || Date.now() - cached.fetchedAt >= FRESH_MS) load(rangeRef.current, { force: true });
        };
        window.addEventListener('focus', handleReturn);
        document.addEventListener('visibilitychange', handleReturn);
        return () => {
            window.removeEventListener('focus', handleReturn);
            document.removeEventListener('visibilitychange', handleReturn);
        };
    }, [load, userId]);

    // Someone changed an assignment or membership that shows on this calendar. Other cached
    // months may be affected too, so they are all dropped, not just the one on screen.
    useEffect(() => {
        let jitterTimer = null;
        const scheduleRefetch = debounce(() => {
            clearTimeout(jitterTimer);
            jitterTimer = setTimeout(refetch, Math.random() * CHANGE_JITTER_MS);
        }, CHANGE_DEBOUNCE_MS);

        const handleChange = () => {
            if (userId) forgetUser(userId);
            scheduleRefetch();
        };

        socket.on('calendarChanged', handleChange);
        return () => {
            socket.off('calendarChanged', handleChange);
            scheduleRefetch.cancel();
            clearTimeout(jitterTimer);
        };
    }, [refetch, userId]);

    useEffect(() => () => {
        requestIdRef.current += 1;
        abortRef.current?.abort();
    }, []);

    return { ...data, loaded, loading, error, setRange, refetch };
};

export default useCalendarEvents;
